import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { sanitizeString } from '../utils/sanitization';
import { isAllowedRedirectPath } from '../constants/routing';

export type AuthFeature = 'add_to_cart' | 'buy_now' | 'message_seller' | 'wishlist' | 'review' | 'checkout' | 'general';

// SECURITY: Validate redirect path to prevent open redirect attacks
function validateRedirectPath(path: string): string {
  if (!path || typeof path !== 'string') {
    return '/';
  }
  
  // SECURITY: Sanitize path
  const sanitized = sanitizeString(path, 500) || '/';
  
  // SECURITY: Normalize path (remove double slashes, trailing slashes for comparison)
  let normalized = sanitized.replace(/\/+/g, '/');
  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }
  
  // SECURITY: Check for path traversal
  if (normalized.includes('..') || normalized.includes('//')) {
    return '/';
  }
  
  // SECURITY: Validate path is allowed using shared constant (whitelist approach)
  if (!isAllowedRedirectPath(normalized)) {
    return '/';
  }
  
  return sanitized;
}

// SECURITY: Get safe current pathname
function getSafeCurrentPath(): string {
  try {
    const pathname = window.location.pathname;
    return validateRedirectPath(pathname);
  } catch {
    return '/';
  }
}

interface UseAuthPromptReturn {
  showAuthPrompt: (feature: AuthFeature, redirectTo?: string) => void;
  isOpen: boolean;
  feature: AuthFeature | null;
  redirectTo: string | null;
  closeAuthPrompt: () => void;
  handleSignIn: () => void;
  handleSignUp: () => void;
  modalProps: {
    isOpen: boolean;
    feature: AuthFeature | null;
    onClose: () => void;
    onSignIn: () => void;
    onSignUp: () => void;
  };
}

export function useAuthPrompt(): UseAuthPromptReturn {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [feature, setFeature] = useState<AuthFeature | null>(null);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  const showAuthPrompt = useCallback((feat: AuthFeature, redirect?: string) => {
    setFeature(feat);
    // SECURITY: Validate redirect path to prevent open redirect attacks
    const safeRedirect = redirect ? validateRedirectPath(redirect) : getSafeCurrentPath();
    setRedirectTo(safeRedirect);
    setIsOpen(true);
  }, []);

  const closeAuthPrompt = useCallback(() => {
    setIsOpen(false);
    // Clear state after animation
    setTimeout(() => {
      setFeature(null);
      setRedirectTo(null);
    }, 200);
  }, []);

  const handleSignIn = useCallback(() => {
    closeAuthPrompt();
    // SECURITY: Validate redirect path before navigation
    const safeRedirect = redirectTo ? validateRedirectPath(redirectTo) : getSafeCurrentPath();
    navigate('/login', { state: { redirectTo: safeRedirect } });
  }, [navigate, redirectTo, closeAuthPrompt]);

  const handleSignUp = useCallback(() => {
    closeAuthPrompt();
    // SECURITY: Validate redirect path before navigation
    const safeRedirect = redirectTo ? validateRedirectPath(redirectTo) : getSafeCurrentPath();
    navigate('/register', { state: { redirectTo: safeRedirect } });
  }, [navigate, redirectTo, closeAuthPrompt]);

  const modalProps = {
    isOpen,
    feature,
    onClose: closeAuthPrompt,
    onSignIn: handleSignIn,
    onSignUp: handleSignUp,
  };

  return {
    showAuthPrompt,
    isOpen,
    feature,
    redirectTo,
    closeAuthPrompt,
    handleSignIn,
    handleSignUp,
    modalProps,
  };
}

